// One BOM revision: header (revision, release, CM, project), then tabs: Lines (spreadsheet editing,
// conditions as chips, a side panel per line, lines grouped by module with packaging in its own group),
// Matrix (parts × variants, click a cell to change one variant), Board (a PCBA's components from its KiCad schematics,
// and who supplies each: BoardTab.tsx) and Costs (each variant's flat BOM with its roll-up). Only drafts change. It opens
// on the BOMs page or inside a project's Build tab (`useBomPlace()`: its address-bar names and the way back).
import { AlertTriangle, ArrowLeft, Boxes, ChevronDown, ChevronRight, CopyPlus, Package, PackageCheck, Trash2, Undo2, Wand2, X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  type BomIndex,
  type BomItem,
  type BomLineRow,
  type BomRow,
  type Buildable,
  bomOf,
  builtInside,
  byCm,
  type Conditions,
  conditionsOf,
  type FamilySummary,
  formatConditions,
  isPackagingRow,
  linePartIds,
  onHandFor,
  ownerLabel,
  parseConditions,
  productOptions,
  revisionState,
  SUPPLY_LABELS,
  type Supply,
  summarizeFamily,
  variantOptions,
  variantsOf,
} from "../../../shared/boms.ts";
import type { Bom, Item, Project } from "../../../shared/db.gen.ts";
import { BOM_SUPPLIES } from "../../../shared/mutations.ts";
import { memberLabel } from "../../../shared/part-groups.ts"; // Part groups ---
import { typeLabel } from "../../../shared/part-type.ts";
import { NO_SECTION, PACKAGING_SECTION, type Packaging, packagingOf, partProjects, projectPath, sectionsOf } from "../../../shared/projects.ts";
import { cost2, costRange, itemBySku, marginTone, parseQty, pct, pctRange, STATE_TONE, useBomIndex, useBomPlace } from "../lib/boms.ts";
import { money, qty, options as toOptions } from "../lib/format.ts";
import { can, mutate, mutateWithResult } from "../lib/mutate.ts";
import { useProjectIndex, useUsedIn } from "../lib/projects.ts"; // Manufacturing projects ---
import { Link, navigate, setSearch, useSearch } from "../lib/router.tsx";
import { me } from "../lib/session.ts";
import { useRow, useRows } from "../lib/sync.ts";
import { Badge, Button, cx, EmptyState, Input, Select } from "../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../ui/DataTable.tsx";
import { Tabs } from "../ui/PageParts.tsx";
import { Popover } from "../ui/Popover.tsx";
import { Fields, Section } from "../ui/RecordPanel.tsx";
import { openPane, PaneLink, paneBack, usePane } from "../ui/SidePane.tsx";
import { toast } from "../ui/toast.tsx";
import { BoardTab, partsOn, useBoard, useBoardProject, useBoardSplit } from "./BoardTab.tsx"; // Board components ---
import { CompareTab } from "./BomCompare.tsx";
import { FillDialog, LineGroupChip, lineGroupOf, lineGroupText, successorOf } from "./BomFill.tsx"; // Part groups ---
import { BomMatrix } from "./BomMatrix.tsx";
import { PartPicker } from "./BomPicker.tsx";
import { ReplacePartDialog } from "./BomReplace.tsx";
import { BomCharges } from "./Charges.tsx"; // Charges ---
import { itemPath } from "./ItemsPage.tsx";
import { usePartGroups } from "./PartGroups.tsx"; // Part groups ---
import { PartProjectChips } from "./projects/ProjectChips.tsx"; // Manufacturing projects ---
import { statusOf, useSpecIndex } from "./specs/lib.ts"; // One revision per item ---
import { NoSpec, SpecWorkspace } from "./specs/SpecWorkspace.tsx";

type Tab = "lines" | "matrix" | "board" | "spec" | "charges" | "costs" | "compare";
type ProductOption = { name: string; values: string[] };

const supplyOptions = BOM_SUPPLIES.map((s) => ({ value: s, label: SUPPLY_LABELS[s] }));

/** Variants in option order (Wireless before Wired, then by color as Shopify lists them). */
function orderVariants(variants: Item[] | readonly Item[], options: ProductOption[]) {
  const rank = (v: Item) => {
    const opts = variantOptions(v);
    return options.map((o) => o.values.indexOf(opts[o.name] ?? ""));
  };
  return [...variants].sort((a, b) => {
    const [x, y] = [rank(a), rank(b)];
    for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return x[i] - y[i];
    return a.sku.localeCompare(b.sku, undefined, { numeric: true });
  });
}

export function BomEditor({ id }: { id: string }) {
  const ix = useBomIndex();
  const row = useRow("bom", id);
  const params = useSearch();
  const place = useBomPlace();
  const index = ix.bomById.get(id);
  const variantParam = params.get("variant");
  const tab = (params.get(place.tab) as Tab | null) ?? (variantParam ? "costs" : "lines");

  const variants = useMemo(() => (index ? (variantsOf(ix, index) as unknown as Item[]) : []), [ix, index]);
  const options = useMemo(() => (index?.shopify_product_id ? productOptions(variants as never) : []), [index, variants]);
  const ordered = useMemo(() => orderVariants(variants, options), [variants, options]);
  const summary = useMemo(() => (index ? summarizeFamily(ix, index) : null), [ix, index]);
  // Board components --- a sub-assembly's board, from the KiCad project its spec keeps (a PCBA shows the tab without one).
  const project = useBoardProject(index?.item_id);
  const board = useBoard(project?.id)?.board;
  const pcba = !!project || ix.itemById.get(index?.item_id ?? "")?.part_type === "pcba";
  // Table views --- the tab's table's View and Columns menus sit at the end of the tab bar
  // One revision per item --- a sub-assembly's or a product's spec is part of what this BOM releases, so it's a tab here
  // (a product's shown with its first variant, as the Specs page lists it).
  const sx = useSpecIndex();
  const spec = row?.item_id ? sx.specByItem.get(row.item_id) : sx.specByProduct.get(row?.shopify_product_id ?? "");
  const specItem = useRow("item", row?.item_id ?? (spec && sx.itemOfSpec.get(spec.id)) ?? ordered[0]?.id);
  const specStatus = spec ? statusOf(sx, spec) : null;

  if (!row || !index || !summary)
    return (
      <EmptyState title="This BOM doesn't exist">
        <Link to={place.back.to} className="text-accent-text hover:underline">
          {place.back.label}
        </Link>
      </EmptyState>
    );

  const editable = row.status === "draft" && can("bom_line.update");
  const product = !!row.shopify_product_id;
  const lineCount = ix.linesByBom.get(id)?.length ?? 0;
  const chargeCount = ix.chargesByFamily.get(summary.key)?.length ?? 0; // Charges ---
  const tabs: [Tab, string][] = [
    ["lines", `Lines · ${lineCount}`],
    ...(product ? [["matrix", "Matrix"] as [Tab, string]] : []),
    ...(pcba ? [["board", board ? `Board · ${partsOn(board)}` : "Board"] as [Tab, string]] : []),
    [
      "spec",
      !specStatus || specStatus.kind === "none"
        ? "Spec"
        : specStatus.kind === "draft"
          ? "Spec · draft"
          : `Spec · Rev ${specStatus.rev}${specStatus.kind === "ahead" ? " · edited" : ""}`,
    ],
    ["charges", chargeCount ? `Charges · ${chargeCount}` : "Charges"],
    ["costs", "Costs"],
    ["compare", "Compare"],
  ];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Header ix={ix} bom={row} summary={summary} />
      <InfoStrip bom={row} summary={summary} />
      <Tabs
        level="record"
        tabs={tabs.map(([key, label]) => ({ key, label }))}
        active={tab}
        onPick={(t) => setSearch({ [place.tab]: t === "lines" && !variantParam ? null : t })}
        trailing={
          !editable &&
          row.status !== "draft" &&
          tab !== "charges" &&
          tab !== "spec" && (
            <span className="text-[12px] text-faint">
              {row.status === "active"
                ? `Rev ${row.revision} is active: start a new revision to change its parts (Made by and Supply change here)`
                : `Rev ${row.revision} is ${row.status}: start a new revision to change it`}
            </span>
          )
        }
      />
      <div className="min-h-0 flex-1 bg-surface">
        {tab === "lines" && <LinesTab ix={ix} bom={index} editable={editable} options={options} summary={summary} />}
        {tab === "matrix" && product && <BomMatrix ix={ix} bom={index} variants={ordered} options={options} editable={editable} summary={summary} />}
        {tab === "board" && pcba && <BoardTab ix={ix} bom={index} editable={editable} project={project} />}
        {tab === "spec" && specItem && (
          <div className="flex h-full min-h-0 flex-col pt-2">
            {spec ? (
              <SpecWorkspace key={spec.id} item={specItem} spec={spec} ix={sx} wide inBom releasedAbove={row.status === "draft" && can("bom.release")} />
            ) : (
              <NoSpec item={specItem} />
            )}
          </div>
        )}
        {tab === "charges" && <BomCharges bom={row} />}
        {tab === "costs" && <CostsTab ix={ix} summary={summary} variants={ordered} variantId={variantParam} />}
        {tab === "compare" && <CompareTab key={index.id} ix={ix} bom={index} variants={ordered} />}
      </div>
    </div>
  );
}

// Header -------------------------------------------------------------------------------------------

function Header({ ix, bom, summary }: { ix: BomIndex; bom: Bom; summary: FamilySummary }) {
  const family = ix.families.get(summary.key) ?? [];
  const state = revisionState(ix, bom);
  const [releasing, setReleasing] = useState(false);
  // Today where the user is (en-CA formats as YYYY-MM-DD), or the later date a draft was planned for (Replace part).
  const planned = bom.status === "draft" ? bom.effective_from : null;
  const [date, setDate] = useState(() => {
    const today = new Date().toLocaleDateString("en-CA");
    return planned && planned > today ? planned : today;
  });
  const newerDraft = family.find((b) => b.status === "draft" && b.id !== bom.id);
  const now = () => new Date().toISOString();
  const place = useBomPlace();
  // Board components --- what releasing tells the maker to buy, said before it goes
  const split = useBoardSplit(ix, bom);
  const makerOf = useRow("vendor", bom.make_vendor_id ?? undefined);
  // One numbered revision per item (server/src/item-release.ts): a sub-assembly's spec is released with its BOM under
  // the item's next number; only its first release goes without a note.
  const sx = useSpecIndex();
  const spec = bom.item_id ? sx.specByItem.get(bom.item_id) : sx.specByProduct.get(bom.shopify_product_id ?? "");
  const specRevs = spec ? (sx.revisionsBySpec.get(spec.id) ?? []) : [];
  const released = family.filter((b) => b.status !== "draft");
  const number = Math.max(bom.revision, Math.max(0, ...specRevs.map((r) => Number(r.rev) || 0)) + 1, Math.max(0, ...released.map((b) => b.revision)) + 1);
  const first = !released.length && !spec?.current_revision_id;
  const [note, setNote] = useState("");

  const release = async () => {
    if (!first && !note.trim()) return toast.error("Say what changed in this revision");
    setReleasing(false);
    const res = await mutateWithResult("bom.release", { id: bom.id, effective_from: date, note }, [
      { t: "bom", id: bom.id, row: { ...bom, status: "active", effective_from: date, updated_at: now() } },
    ]);
    if (res.ok) toast.ok(`Rev ${number} released${spec ? ", its spec with it" : ""}`);
  };
  const revise = async () => {
    const id = crypto.randomUUID();
    const revision = (family[0]?.revision ?? bom.revision) + 1;
    const res = await mutateWithResult("bom.revise", { id, from: bom.id }, [
      { t: "bom", id, row: { ...bom, id, revision, status: "draft", effective_from: null, created_at: now(), updated_at: now() } },
    ]);
    if (res.ok) setSearch({ [place.open]: id, line: null });
  };
  // Back to draft: an active revision no make order or run has used (the server says what did, if anything).
  const unrelease = async () => {
    if (!confirm(`Put Rev ${bom.revision} back to draft? It can be changed again, then released when it's ready.`)) return;
    const res = await mutateWithResult("bom.unrelease", { id: bom.id }, [
      { t: "bom", id: bom.id, row: { ...bom, status: "draft", effective_from: null, updated_at: now() } },
    ]);
    if (res.ok) toast.ok(`Rev ${bom.revision} is a draft again`);
  };
  const retire = async () => {
    if (!confirm(`Retire Rev ${bom.revision}? Nothing will use it any more.`)) return;
    await mutateWithResult("bom.retire", { id: bom.id }, [{ t: "bom", id: bom.id, row: { ...bom, status: "retired", updated_at: now() } }]);
  };
  const remove = async () => {
    // A released or retired revision (admins): only one nothing has used; the server says what did, if anything.
    const ask =
      bom.status === "draft"
        ? `Delete draft Rev ${bom.revision} and its lines?`
        : `Delete ${bom.status} Rev ${bom.revision} and its lines for good? History keeps a record of them.${
            state === "active" ? " It's the current revision: until another is released, this has no released BOM." : ""
          }`;
    if (!confirm(ask)) return;
    const other = family.find((b) => b.id !== bom.id);
    if (await mutate("bom.remove", { id: bom.id })) navigate(other ? place.path(other.id) : place.back.to);
  };
  const canDelete = can("bom.remove") && (bom.status === "draft" || me()?.role === "admin");

  return (
    <header className="flex h-12 shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
      <Link to={place.back.to} className="rounded p-1 text-muted hover:bg-hover hover:text-text" title={place.back.label}>
        <ArrowLeft className="size-4" />
      </Link>
      <h1 className="min-w-0 truncate text-[15px] font-semibold">{ownerLabel(ix, bom)}</h1>
      <span className="shrink-0 whitespace-nowrap text-muted">
        {bom.shopify_product_id ? `Shopify product · ${summary.variants.length} variants` : "Sub-assembly"}
      </span>
      <div className="w-40 shrink-0">
        <Select
          value={bom.id}
          onChange={(e) => setSearch({ [place.open]: e.target.value, line: null })}
          options={family.map((b) => ({ value: b.id, label: `Rev ${b.revision} · ${revisionState(ix, b)}` }))}
          className="h-7 text-[12px]"
        />
      </div>
      <Badge tone={STATE_TONE[state]}>{state === "scheduled" ? `from ${bom.effective_from}` : state}</Badge>
      {planned && <span className="shrink-0 text-[12px] whitespace-nowrap text-faint">planned from {planned}</span>}
      {bom.status === "active" && bom.effective_from && state !== "scheduled" && (
        <span className="shrink-0 text-[12px] whitespace-nowrap text-faint">since {bom.effective_from}</span>
      )}
      <div className="ml-auto flex items-center gap-2">
        {can("bom.release") && bom.status === "draft" && !releasing && (
          <Button variant="primary" size="sm" icon={<PackageCheck className="size-3.5" />} onClick={() => setReleasing(true)}>
            Release
          </Button>
        )}
        {releasing && (
          <>
            {split && (
              <span className="text-[12px] whitespace-nowrap text-muted" title="From the board's KiCad project and this revision's lines">
                Board: {split.total} components · HHL sends {split.hhl} · {makerOf?.short_name || makerOf?.name || "the maker"} buys {split.maker}
              </span>
            )}
            <Input
              autoFocus
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && release()}
              placeholder={first ? "Note (optional: its first release)" : "What changed?"}
              title={spec ? "Its spec is released with it, under the same number: vendors see the note" : undefined}
              className="h-7 w-56"
            />
            <span className="whitespace-nowrap text-muted">In use from</span>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-7 w-36" />
            <Button variant="primary" size="sm" onClick={release} title={spec ? "The BOM and its spec, together" : undefined}>
              Release Rev {number}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setReleasing(false)}>
              Cancel
            </Button>
          </>
        )}
        {can("bom.revise") && bom.status !== "draft" && newerDraft && (
          <Button size="sm" onClick={() => setSearch({ [place.open]: newerDraft.id, line: null })}>
            Open draft Rev {newerDraft.revision}
          </Button>
        )}
        {can("bom.revise") && bom.status !== "draft" && !newerDraft && (
          <Button size="sm" icon={<CopyPlus className="size-3.5" />} onClick={revise} title="Copy this revision into a new draft to change it">
            New revision
          </Button>
        )}
        {can("bom.unrelease") && bom.status === "active" && !newerDraft && (
          <Button
            size="sm"
            variant="ghost"
            icon={<Undo2 className="size-3.5" />}
            onClick={unrelease}
            title="Change it again (only a revision no make order or run has used)"
          >
            Back to draft
          </Button>
        )}
        {can("bom.retire") && bom.status === "active" && (
          <Button size="sm" variant="ghost" onClick={retire}>
            Retire
          </Button>
        )}
        {canDelete && (
          <Button
            size="sm"
            variant="ghost"
            icon={<Trash2 className="size-3.5" />}
            onClick={remove}
            title={bom.status === "draft" ? "Delete this draft" : "Delete this revision (only one nothing has used: no make order or run)"}
          >
            Delete
          </Button>
        )}
      </div>
    </header>
  );
}

/** A kit's or a board's own projects, ticked in a list beside it; each tick saves (migration 044: it can be in several). */
function ProjectsPicker({
  value,
  projects,
  disabled,
  onChange,
}: {
  value: string[];
  projects: readonly Project[];
  disabled: boolean;
  onChange: (ids: string[]) => void;
}) {
  const chosen = projects
    .filter((p) => value.includes(p.id))
    .map((p) => p.name)
    .join(", ");
  return (
    <Popover
      width={220}
      button={(toggle, open) => (
        <button
          type="button"
          onClick={toggle}
          disabled={disabled}
          aria-expanded={open}
          title={chosen || undefined}
          className="flex h-7 w-full items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 text-left text-[12px] hover:border-line-strong disabled:pointer-events-none disabled:opacity-50"
        >
          <span className={cx("min-w-0 flex-1 truncate", !chosen && "text-faint")}>{chosen || "No project"}</span>
          <ChevronDown className="size-3.5 shrink-0 text-faint" />
        </button>
      )}
    >
      {() => (
        <div className="flex max-h-80 flex-col gap-0.5 overflow-y-auto text-[12px]">
          {projects.map((p) => (
            <label key={p.id} className="flex items-center gap-2 rounded px-1.5 py-1 hover:bg-hover">
              <input
                type="checkbox"
                checked={value.includes(p.id)}
                onChange={(e) => onChange(e.target.checked ? [...value, p.id] : value.filter((id) => id !== p.id))}
                className="accent-[var(--accent)]"
              />
              {p.name}
            </label>
          ))}
        </div>
      )}
    </Popover>
  );
}

/**
 * Project, CM, phantom and notes on the left; what the family costs and what needs fixing on the right. Manufacturing
 * projects --- a sub-assembly something uses (a newer draft too: it joins that project once released) is in the
 * projects of what uses it, shown as chips; its own Projects count only while nothing uses it (a kit, the dongle), and
 * it can be in several. A product BOM's is its product's link while it has one, else its own Project select: one or
 * none, since the product is the finished item.
 */
function InfoStrip({ bom, summary }: { bom: Bom; summary: FamilySummary }) {
  const projects = useRows("project");
  const vendors = useRows("vendor");
  const px = useProjectIndex();
  const usedIn = useUsedIn();
  const fromUse = bom.item_id ? partProjects(px).get(bom.item_id) : undefined;
  const used = !!bom.item_id && (usedIn.has(bom.item_id) || !!fromUse?.onDraft.length);
  const link = bom.shopify_product_id ? px.linkByProduct.get(bom.shopify_product_id) : undefined;
  const linked = link ? px.projectById.get(link.project_id) : undefined;
  const editable = can("bom.update");
  const cms = [...vendors]
    .filter((v) => !v.archived)
    .sort((a, b) => Number(a.kind === "supplier") - Number(b.kind === "supplier") || a.name.localeCompare(b.name));
  const save = (patch: Parameters<typeof mutate<"bom.update">>[1]["patch"]) => mutate("bom.update", { id: bom.id, patch });
  const f = summary.flags;
  const names = (items: { sku: string; name: string }[]) => items.map((i) => i.sku || i.name).join(", ");
  const label = (text: string, control: ReactNode, wide = true) => (
    <label className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
      <span className="text-muted">{text}</span>
      <span className={wide ? "w-44" : ""}>{control}</span>
    </label>
  );
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 border-b border-line bg-surface-2/50 px-4 py-1.5 text-[12px]">
      {used && bom.item_id ? (
        <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap" title="A sub-assembly is in the projects whose BOMs use it">
          <span className="text-muted">Projects (from what uses it)</span>
          {fromUse?.inUse.length || fromUse?.onDraft.length ? <PartProjectChips px={px} id={bom.item_id} /> : <span className="text-faint">In no project</span>}
        </span>
      ) : linked ? (
        <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
          <span className="text-muted">Project</span>
          <Link to={projectPath(linked, "products")} className="text-accent-text hover:underline" title="Its product is linked on the project's Products tab">
            {linked.name}
          </Link>
          <span className="text-faint">(from its product's link)</span>
        </span>
      ) : (
        <>
          {bom.item_id
            ? label(
                "Projects",
                <ProjectsPicker value={bom.project_ids} projects={projects} disabled={!editable} onChange={(ids) => save({ project_ids: ids })} />,
              )
            : // A Shopify product is the finished item: one project or none
              label(
                "Project",
                <Select
                  value={bom.project_ids[0] ?? ""}
                  disabled={!editable}
                  onChange={(e) => save({ project_ids: e.target.value ? [e.target.value] : [] })}
                  options={[{ value: "", label: "No project" }, ...projects.map((p) => ({ value: p.id, label: p.name }))]}
                  className="h-7 text-[12px]"
                />,
              )}
          {bom.item_id && <span className="-ml-2 shrink-0 text-faint">Count only while nothing uses this sub-assembly</span>}
        </>
      )}
      {label(
        "Made by",
        <Select
          value={bom.make_vendor_id ?? ""}
          // Sourcing, not part of a revision: on the one in use and its draft alike (bom.set_maker)
          disabled={!can("bom.set_maker") || bom.status === "retired"}
          onChange={(e) => mutate("bom.set_maker", { bom_id: bom.id, make_vendor_id: e.target.value || null })}
          // A sub-assembly with no maker of its own is built in the same order as what uses it (builtInside()).
          options={[
            { value: "", label: bom.item_id ? "Whoever builds what uses it" : "No CM yet" },
            ...cms.map((v) => ({ value: v.id, label: v.short_name || v.name })),
          ]}
          className="h-7 text-[12px]"
        />,
      )}
      <NotesInput bom={bom} disabled={!editable} />
      <div className="ml-auto flex items-center gap-3">
        {f.missingCost.length > 0 && (
          <span title={`No cost: ${names(f.missingCost)}`}>
            <Badge tone="warn">{f.missingCost.length} no cost</Badge>
          </span>
        )}
        {f.missingVendor.length > 0 && (
          <span title={`HHL buys these but they have no vendor: ${names(f.missingVendor)}`}>
            <Badge tone="warn">{f.missingVendor.length} no vendor</Badge>
          </span>
        )}
        {f.endOfLife.length > 0 && (
          <span title={`End of life or obsolete: ${names(f.endOfLife)}`}>
            <Badge tone="danger">{f.endOfLife.length} EOL</Badge>
          </span>
        )}
        {f.warnings.length > 0 && (
          <span title={f.warnings.join("\n")}>
            <Badge tone="danger">{f.warnings.length} warnings</Badge>
          </span>
        )}
        <span className="text-muted">
          Cost <span className="font-mono text-text">{costRange(summary.cost) || "—"}</span>
          {!f.missingCost.length ? "" : "+"}
        </span>
        {summary.price && (
          <span className="text-muted">
            Price <span className="font-mono text-text">{costRange(summary.price)}</span>
          </span>
        )}
        {summary.price && (
          <span className="text-muted">
            Margin <span className={cx("font-mono", marginTone(summary.margin?.min))}>{pctRange(summary.margin) || "—"}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function NotesInput({ bom, disabled }: { bom: Bom; disabled: boolean }) {
  const [draft, setDraft] = useState(bom.notes);
  useEffect(() => setDraft(bom.notes), [bom.notes]);
  const save = () => draft !== bom.notes && mutate("bom.update", { id: bom.id, patch: { notes: draft } });
  return (
    <Input
      value={draft}
      disabled={disabled}
      placeholder="Notes"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className="h-7 w-64 border-transparent bg-transparent text-[12px] hover:border-line"
    />
  );
}

// Lines --------------------------------------------------------------------------------------------

function ConditionChips({ conditions }: { conditions: Conditions }) {
  const entries = Object.entries(conditions);
  if (!entries.length) return <span className="text-faint">All variants</span>;
  return (
    <span className="flex min-w-0 gap-1">
      {entries.map(([name, values]) => (
        <Badge key={name} tone="accent">
          {name} = {values.join(", ")}
        </Badge>
      ))}
    </span>
  );
}

// Packaging --- a packaging part's lines are packaging because of the part (its own, or one its option picks), so
// they're always in the Packaging group and the packaging cost layer; any other line can be counted as packaging
// (bom_line.is_packaging: a screwdriver in the box).
type LinePack = { pack: Packaging | null; byPart: boolean };

/** null: not a packaging line. The editor's index holds whole item rows (useBomIndex), pack_type and pack_print too. */
function linePackaging(ix: BomIndex, line: BomLineRow): LinePack | null {
  if (!isPackagingRow(ix, line)) return null;
  for (const id of linePartIds(ix, line)) {
    const item = ix.itemById.get(id);
    const pack = item ? packagingOf(item) : null;
    if (pack) return { pack, byPart: true };
  }
  return { pack: null, byPart: false };
}

/** "Printed box", "Plain bag": what a packaging part is (its Category and printing); a warning while its Category isn't set. */
/**
 * How a sub-assembly line gets into this build, worked out from who makes what (builtInside()): in the same order (no
 * maker of its own, or this build's), or by its own maker on its own make PO, then sent over.
 */
function SubBadge({ ix, parent, item, supply }: { ix: BomIndex; parent: BomRow; item: Item | BomItem; supply: Supply }) {
  const vendors = useRows("vendor");
  const sub = item.source === "local" ? bomOf(ix, item as BomItem) : undefined;
  if (!sub) return <Badge>has BOM</Badge>;
  if (builtInside(sub, parent.make_vendor_id, supply))
    return (
      <span title="Built in the same order as this, by the same maker (any already in stock is used first)">
        <Badge>built in the same order</Badge>
      </span>
    );
  const v = vendors.find((x) => x.id === sub.make_vendor_id);
  return (
    <span title="Built on its own make PO, then sent to this build's maker">
      <Badge>{`built by ${v?.short_name || v?.name || "its maker"} on its own`}</Badge>
    </span>
  );
}

function PackChip({ pack }: { pack: Packaging }) {
  return (
    <span className="shrink-0" title={pack.says}>
      <Badge tone={pack.type ? "neutral" : "warn"}>
        <Package className="mr-1 size-3" />
        {pack.type ? pack.title : "Packaging · Category not set"}
      </Badge>
    </span>
  );
}

/** A line's module, spelled like one the revision has already. "Packaging" counts the line as packaging instead. */
function saveModule(ix: BomIndex, bom: BomRow, line: BomLineRow, text: string) {
  const name = text.trim();
  if (name.toLowerCase() === PACKAGING_SECTION.toLowerCase()) {
    // Packaging --- the Packaging group holds packaging lines only, so putting a line there counts it as packaging.
    toast.info("Counted as packaging: it's in the Packaging group and the packaging cost layer");
    return mutate("bom_line.update", { id: line.id, patch: { is_packaging: true } });
  }
  const known = [...(bom.section_order ?? []), ...(ix.linesByBom.get(bom.id) ?? []).map((l) => l.section ?? "")]
    .map((s) => s.trim())
    .find((s) => s.toLowerCase() === name.toLowerCase());
  // Moved out of the Packaging group: no longer counted as packaging.
  return mutate("bom_line.update", { id: line.id, patch: { section: known ?? name, ...(line.is_packaging ? { is_packaging: false } : {}) } });
}

function LinesTab({ ix, bom, editable, options, summary }: { ix: BomIndex; bom: BomRow; editable: boolean; options: ProductOption[]; summary: FamilySummary }) {
  const lines = useMemo(() => ix.linesByBom.get(bom.id) ?? [], [ix, bom.id]);
  // Not a draft: only who supplies each part changes (sourcing), the rest takes a new revision.
  const sourcing = !editable && bom.status !== "retired" && can("bom.set_line_supply");
  // The line open in the side pane (its first level), lit in the table; an old ?line= link opens there too.
  const pane = usePane();
  const openLine = pane[0]?.kind === "line" ? pane[0].id : null;
  const legacyLine = useSearch().get("line");
  useEffect(() => {
    if (!legacyLine) return;
    setSearch({ line: null });
    openPane({ kind: "line", id: legacyLine }, { root: true, replace: true });
  }, [legacyLine]);
  const links = useRows("vendor_item");
  const product = !!bom.shopify_product_id;
  const packs = useMemo(() => new Map(lines.map((l) => [l.id, linePackaging(ix, l)])), [ix, lines]);
  // Part groups --- option lines named by their group; an end-of-life part offers its current sibling (Replace part)
  const pg = usePartGroups();
  const [replacing, setReplacing] = useState<{ from: string; to: string } | null>(null);

  // Lines in labelled groups, in sectionsOf()'s order: the modules, lines in none, and Packaging (where the module
  // order puts it, else last). A BOM with neither modules nor packaging is one plain list.
  const bands = useMemo(() => {
    const sections = sectionsOf(bom, lines, (l) => !!packs.get(l.id));
    if (sections.length === 1 && !sections[0].packaging) return null;
    const modules = sections.some((s) => s.name !== NO_SECTION && !s.packaging);
    const packaging = (
      <span className="flex items-center gap-1.5">
        <Package className="size-3.5" />
        {PACKAGING_SECTION}
      </span>
    );
    const byLine = new Map<string, TableSection>();
    for (const [order, s] of sections.entries()) {
      const name = s.name === NO_SECTION ? (modules ? "Not in a module" : "Parts") : s.name;
      const band = { key: s.packaging ? "packaging" : `m:${s.name}`, label: s.packaging ? packaging : name, order };
      for (const l of s.lines) byLine.set(l.id, band);
    }
    return byLine;
  }, [bom, lines, packs]);
  const sectionOf = useCallback((l: BomLineRow) => bands?.get(l.id) ?? null, [bands]);

  const columns = useMemo<Column<BomLineRow>[]>(() => {
    const part = (l: BomLineRow) => ix.itemById.get(l.component_item_id ?? "");
    // What one of the line's part costs, across the variants that use it.
    const unitCosts = new Map<string, (number | null)[]>();
    for (const v of summary.variants)
      for (const l of v.resolved.lines) if (l.depth === 0) unitCosts.set(l.lineId, [...(unitCosts.get(l.lineId) ?? []), l.costComplete ? l.unitCost : null]);
    const update = (l: BomLineRow, patch: Parameters<typeof mutate<"bom_line.update">>[1]["patch"]) => mutate("bom_line.update", { id: l.id, patch });
    const number = (field: "qty" | "scrap_pct" | "position", label: string) => ({
      get: (l: BomLineRow) => String(l[field]),
      save: (l: BomLineRow, text: string) => {
        const n = parseQty(text);
        if (n === undefined || (field === "position" && !Number.isInteger(n)))
          return toast.error(`${label}: enter a ${field === "position" ? "whole " : ""}number`);
        update(l, { [field]: n });
      },
    });
    const mapCount = (l: BomLineRow) => ix.mapsByLine.get(l.id)?.size ?? 0;
    return [
      { key: "position", header: "#", width: "56px", align: "right", value: (l) => l.position, edit: number("position", "#") },
      {
        key: "sku",
        header: "SKU",
        width: "170px",
        value: (l) => part(l)?.sku ?? "",
        cell: (l) => (
          <span className="truncate font-mono text-[12px]">
            {part(l)?.sku ?? (l.option_name ? <span className="font-sans text-faint">by {l.option_name}</span> : "")}
          </span>
        ),
        edit: {
          get: (l) => part(l)?.sku ?? "",
          save: (l, text) => {
            if (!text.trim()) return update(l, { component_item_id: null });
            const item = itemBySku(text);
            if (!item) return toast.error(`No part with SKU ${text.trim()}`);
            update(l, { component_item_id: item.id });
          },
        },
      },
      {
        key: "name",
        header: "Name",
        width: "minmax(220px,2fr)",
        value: (l) => part(l)?.name ?? "",
        cell: (l) => {
          const item = part(l);
          const sub = item && ix.families.has(item.source === "shopify" ? `product:${item.shopify_product_id}` : `item:${item.id}`);
          const pack = packs.get(l.id)?.pack;
          const group = lineGroupOf(ix, l, pg);
          const next = successorOf(item as Item | undefined, pg);
          return (
            <span className="flex min-w-0 items-center gap-1.5">
              {group ? (
                // Part groups --- an option line named by its group: "GCU2 box sleeve · Wireless · by Color (5)", else its part
                <span className="flex min-w-0 items-center gap-1.5" title={item ? `${item.name} for every ${l.option_name} not mapped` : undefined}>
                  <Boxes className="size-3.5 shrink-0 text-faint" />
                  <span className="truncate">
                    {lineGroupText(group, l)} · by {l.option_name} ({mapCount(l)})
                    {item && <span className="text-faint">, else {memberLabel(group, item as Item) || item.sku}</span>}
                  </span>
                </span>
              ) : (
                <>
                  <span className="truncate">
                    {item ? (
                      <PaneLink
                        kind="item"
                        id={item.id}
                        to={itemPath(item)}
                        className="hover:text-accent-text hover:underline"
                        title={`Open ${item.sku || item.name} beside the BOM`}
                      >
                        {item.name}
                      </PaneLink>
                    ) : l.option_name ? (
                      `${mapCount(l)} parts by ${l.option_name}`
                    ) : (
                      <span className="text-danger">Pick a part</span>
                    )}
                  </span>
                  {l.option_name && item && <span className="shrink-0 text-faint">· else for other {l.option_name}s</span>}
                </>
              )}
              {pack && <PackChip pack={pack} />}
              {sub && item && <SubBadge ix={ix} parent={bom} item={item} supply={l.supply} />}
              {(ix.overridesByLine.get(l.id)?.size ?? 0) > 0 && <Badge tone="accent">{ix.overridesByLine.get(l.id)?.size} changed for one variant</Badge>}
              {item?.kind === "service" && <Badge>service</Badge>}
              {(item?.lifecycle === "eol" || item?.lifecycle === "obsolete") && <Badge tone="danger">{item.lifecycle.toUpperCase()}</Badge>}
              {item && next && (
                <button
                  type="button"
                  title={`${next.sku} is its current part of the same ${pg.byId.get(next.part_group_id ?? "")?.name ?? "group"} values: replace it in the BOMs`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setReplacing({ from: item.id, to: next.id });
                  }}
                  className="inline-flex h-5 shrink-0 items-center rounded bg-accent-soft px-1.5 text-[11px] font-medium text-accent-text hover:underline"
                >
                  Replace with {next.sku}
                </button>
              )}
            </span>
          );
        },
      },
      { key: "qty", header: "Qty", width: "70px", align: "right", value: (l) => Number(l.qty), cell: (l) => qty(l.qty), edit: number("qty", "Qty") },
      {
        key: "scrap_pct",
        header: "Scrap %",
        width: "80px",
        align: "right",
        value: (l) => Number(l.scrap_pct),
        cell: (l) => (Number(l.scrap_pct) ? `${qty(l.scrap_pct)}%` : ""),
        edit: number("scrap_pct", "Scrap"),
      },
      {
        key: "supply",
        header: "Supply",
        width: "120px",
        value: (l) => SUPPLY_LABELS[l.supply],
        cell: (l) => (byCm(l.supply) ? <Badge>{SUPPLY_LABELS[l.supply]}</Badge> : <span className="text-muted">{SUPPLY_LABELS[l.supply]}</span>),
        // Sourcing, not part of a revision: on the one in use and its draft alike (bom.set_line_supply)
        edit: { options: supplyOptions, get: (l) => l.supply, save: (l, v) => mutate("bom.set_line_supply", { line_id: l.id, supply: v as Supply }) },
      },
      {
        key: "applies_when",
        header: "Applies when",
        width: "minmax(200px,1.5fr)",
        value: (l) => formatConditions(conditionsOf(l)),
        cell: (l) => <ConditionChips conditions={conditionsOf(l)} />,
        edit: {
          get: (l) => formatConditions(conditionsOf(l)),
          save: (l, text) => {
            const parsed = parseConditions(text, product ? options : undefined);
            if (!parsed.ok) return toast.error(parsed.error);
            update(l, { applies_when: parsed.value });
          },
        },
      },
      ...(product
        ? [
            {
              key: "option_name",
              header: "Part by option",
              width: "140px",
              cell: (l: BomLineRow) =>
                l.option_name ? (
                  <Badge tone="accent">
                    {l.option_name} → {mapCount(l)}
                  </Badge>
                ) : (
                  ""
                ),
              edit: {
                options: [{ value: "", label: "—" }, ...options.map((o) => ({ value: o.name, label: o.name }))],
                get: (l: BomLineRow) => l.option_name,
                save: (l: BomLineRow, v: string) => update(l, { option_name: v }),
              },
            } satisfies Column<BomLineRow>,
          ]
        : []),
      { key: "ref_designator", header: "Ref", width: "90px", edit: { get: (l) => l.ref_designator, save: (l, t) => update(l, { ref_designator: t.trim() }) } },
      {
        key: "section",
        header: "Module",
        headerTitle: "The line's module: its group here and on the project's Build tab. Packaging lines are always in Packaging.",
        width: "150px",
        value: (l) => (packs.get(l.id) ? PACKAGING_SECTION : (l.section ?? "")),
        cell: (l) =>
          packs.get(l.id) ? (
            <span className="text-faint" title="Packaging lines are always in the Packaging group">
              {PACKAGING_SECTION}
            </span>
          ) : (
            <span className="truncate">{l.section}</span>
          ),
        // Packaging --- a packaging part's line is always in Packaging; any other line goes in (or out) by its Module.
        edit: { can: (l) => !packs.get(l.id)?.byPart, get: (l) => l.section ?? "", save: (l, text) => saveModule(ix, bom, l, text) },
      },
      {
        key: "unit_cost",
        header: "Unit cost",
        width: "110px",
        align: "right",
        value: (l) => (unitCosts.get(l.id) ?? []).find((c) => c != null) ?? null,
        cell: (l) => {
          const costs = unitCosts.get(l.id) ?? [];
          if (!costs.length) return "";
          if (costs.some((c) => c == null)) return <Badge tone="warn">no cost</Badge>;
          const known = (costs as number[]).map((c) => Math.round(c * 10_000) / 10_000);
          const [lo, hi] = [Math.min(...known), Math.max(...known)];
          return lo === hi ? money(lo) : `${money(lo)}–${money(hi)}`;
        },
      },
    ];
  }, [ix, bom, summary, options, product, packs, pg]);
  const shown = useMemo(() => (sourcing ? columns.map((c) => (c.key === "supply" ? c : { ...c, edit: undefined })) : columns), [columns, sourcing]);

  /** A new line gets the next position; parts the CM itself sells default to "CM buys". */
  const add = (item: Item) => {
    const preferred = links.find((l) => l.item_id === item.id && l.is_preferred) ?? links.find((l) => l.item_id === item.id);
    const supply = bom.make_vendor_id && preferred?.vendor_id === bom.make_vendor_id ? "cm_buys" : "hhl_consigns";
    const position = Math.max(0, ...lines.map((l) => l.position)) + 1;
    mutate("bom_line.create", { id: crypto.randomUUID(), bom_id: bom.id, component_item_id: item.id, position, supply });
  };

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        {editable && (
          <div className="flex shrink-0 items-center gap-3 border-b border-line px-4 py-2">
            <PartPicker bom={bom} ix={ix} onPick={add} className="w-[480px]" />
            <span className="text-[12px] text-faint">Parts in this BOM's projects come first, then new parts with their code, then parts sharing tags.</span>
          </div>
        )}
        <div className="min-h-0 flex-1">
          <DataTable
            rows={lines}
            columns={shown}
            editing={editable || sourcing}
            activeId={openLine}
            defaultSort={{ key: "position" }}
            sectionOf={bands ? sectionOf : undefined}
            onOpen={(l) => openPane({ kind: "line", id: l.id }, { root: true, replace: !!openLine })}
            rowNoun={["line", "lines"]}
            exportName="BOM lines"
            pageFilters={false}
            empty={<div className="p-6 text-center text-muted">{editable ? "No lines yet. Add a part above." : "No lines."}</div>}
          />
        </div>
      </div>
      {replacing && <ReplacePartDialog fromId={replacing.from} toId={replacing.to} onClose={() => setReplacing(null)} />}
    </div>
  );
}

/** A BOM line's title in the side pane's trail. */
export function LineTitle({ id }: { id: string }) {
  const line = useRow("bom_line", id);
  return line ? `Line ${line.position}` : "Line";
}

/** A BOM line in the side pane (ui/SidePane.tsx, kind "line"): its part, quantity, supply, packaging, when it applies,
 * its part by option and per-variant changes; its part (and each option's) opens beside it as the next level. */
export function LinePane({ id }: { id: string }) {
  const ix = useBomIndex();
  const line = useRow("bom_line", id);
  const bom = line ? ix.bomById.get(line.bom_id) : undefined;
  const options = useMemo(() => (bom?.shopify_product_id ? productOptions(variantsOf(ix, bom) as never) : []), [ix, bom]);
  if (!bom) return null;
  return <LineBody ix={ix} bom={bom} lineId={id} editable={bom.status === "draft" && can("bom_line.update")} options={options} />;
}

function LineBody({ ix, bom, lineId, editable, options }: { ix: BomIndex; bom: BomRow; lineId: string; editable: boolean; options: ProductOption[] }) {
  const line = useRow("bom_line", lineId);
  const maps = useRows("bom_option_map");
  const overrides = useRows("bom_override");
  const [changing, setChanging] = useState(false);
  const [filling, setFilling] = useState(false); // Part groups --- Fill from group…
  const pg = usePartGroups();
  const close = paneBack;

  if (!line || line.bom_id !== bom.id) return null;
  const part = ix.itemById.get(line.component_item_id ?? "");
  const pack = linePackaging(ix, line);
  const conditions = conditionsOf(line);
  const update = (patch: Parameters<typeof mutate<"bom_line.update">>[1]["patch"]) => mutate("bom_line.update", { id: line.id, patch });
  const toggle = (name: string, value: string) => {
    const current = conditions[name] ?? [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    const { [name]: _, ...rest } = conditions;
    update({ applies_when: next.length ? { ...rest, [name]: next } : rest });
  };
  const option = options.find((o) => o.name === line.option_name);
  const lineMaps = maps.filter((m) => m.bom_line_id === line.id);
  const lineOverrides = overrides.filter((o) => o.bom_line_id === line.id);
  // Part groups --- the parts the line picks now, whose groups the part picker offers first
  const near = [line.component_item_id, ...lineMaps.map((m) => m.component_item_id)];
  const group = lineGroupOf(ix, line, pg);

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="border-b border-line px-4 py-3">
        <div className="truncate text-[15px] font-semibold">
          {part ? (
            <PaneLink kind="item" id={part.id} to={itemPath(part)} className="hover:text-accent-text hover:underline" title="Open the part beside this line">
              {part.name}
              <ChevronRight className="ml-0.5 inline size-4 text-faint" />
            </PaneLink>
          ) : line.option_name ? (
            `Part by ${line.option_name}`
          ) : (
            "No part yet"
          )}
        </div>
        <div className="truncate text-muted">
          {bom.shopify_product_id ? "BOM" : "Sub-assembly BOM"} line {line.position}
          {part ? ` · ${part.sku} · ${typeLabel(part)}` : ""}
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {editable && (
          <div className="px-2 pb-2">
            {changing ? (
              <PartPicker
                bom={bom}
                ix={ix}
                near={near}
                autoFocus
                placeholder="Change the part…"
                onPick={(item) => {
                  setChanging(false);
                  update({ component_item_id: item.id });
                }}
                onCancel={() => setChanging(false)}
              />
            ) : (
              <Button size="sm" onClick={() => setChanging(true)}>
                {part ? "Change part" : "Pick a part"}
              </Button>
            )}
          </div>
        )}
        <Fields
          row={line}
          update="bom_line.update"
          disabled={!editable}
          fields={[
            { key: "qty", label: "Quantity", type: "number", format: qty },
            { key: "scrap_pct", label: "Scrap %", type: "number", format: (v) => (Number(v) ? `${qty(v)}%` : "0%") },
          ]}
        />
        {/* Sourcing, not part of a revision: on the one in use and its draft alike (bom.set_line_supply) */}
        <Fields
          row={line}
          disabled={bom.status === "retired" || !can("bom.set_line_supply")}
          fields={[
            {
              key: "supply",
              label: "Supply",
              type: "select",
              options: toOptions(SUPPLY_LABELS),
              save: (v) => mutate("bom.set_line_supply", { line_id: line.id, supply: v as Supply }),
            },
          ]}
        />
        {/* Packaging --- a packaging part's line is packaging because of its part; any other line by its Module. */}
        {(pack?.byPart || line.is_packaging) && (
          <div className="flex items-start rounded-md px-2 py-1">
            <div className="w-32 shrink-0 text-muted">Packaging</div>
            <div className="min-w-0 flex-1">
              {pack?.pack && <PackChip pack={pack.pack} />}
              <div className="mt-0.5 text-[12px] text-faint">
                {pack?.byPart
                  ? "A packaging part: always in the Packaging group and the packaging cost layer."
                  : "In the Packaging module, so it counts as packaging here (like a screwdriver in the box). Move it to another module to stop."}
              </div>
            </div>
          </div>
        )}
        <Fields
          row={line}
          update="bom_line.update"
          disabled={!editable}
          fields={[
            ...(pack
              ? []
              : [{ key: "section", label: "Module", placeholder: "Not in a module", save: (v: unknown) => saveModule(ix, bom, line, String(v ?? "")) }]),
            { key: "ref_designator", label: "Ref designator", placeholder: "e.g. U1, J2" },
            { key: "notes", label: "Notes", type: "textarea" },
          ]}
        />

        <Section title="Applies when">
          {!bom.shopify_product_id ? (
            <div className="px-2 pb-2 text-muted">
              {Object.keys(conditions).length ? <ConditionChips conditions={conditions} /> : "Every build."} Conditions on a sub-assembly use the options of the
              product it goes into.
            </div>
          ) : (
            <div className="flex flex-col gap-2 px-2 pb-2">
              {options.map((o) => (
                <div key={o.name}>
                  <div className="mb-1 text-[12px] text-muted">
                    {o.name} {!(conditions[o.name]?.length > 0) && <span className="text-faint">· any</span>}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {o.values.map((v) => {
                      const on = conditions[o.name]?.includes(v);
                      return (
                        <button
                          key={v}
                          type="button"
                          disabled={!editable}
                          onClick={() => toggle(o.name, v)}
                          className={cx(
                            "h-6 rounded border px-1.5 text-[12px]",
                            on ? "border-accent bg-accent-soft text-accent-text" : "border-line text-muted hover:text-text",
                          )}
                        >
                          {v}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {!options.length && <div className="text-muted">This product has no options; the line is in every build.</div>}
            </div>
          )}
        </Section>

        {bom.shopify_product_id && options.length > 0 && (
          <Section title="Part by option">
            <div className="flex flex-col gap-1 px-2 pb-2">
              <Select
                value={line.option_name}
                disabled={!editable}
                onChange={(e) => update({ option_name: e.target.value })}
                options={[{ value: "", label: "Same part for every variant" }, ...options.map((o) => ({ value: o.name, label: `By ${o.name}` }))]}
              />
              {/* Part groups --- the line's group, and Fill from group… on a draft */}
              {line.option_name && (group || (editable && pg.groups.length > 0)) && (
                <div className="flex items-center gap-2">
                  {group ? <LineGroupChip group={group} line={line} /> : <span className="text-[12px] text-faint">Its parts aren't in one group yet</span>}
                  {editable && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto"
                      icon={<Wand2 className="size-3.5" />}
                      onClick={() => setFilling(true)}
                      title="Pick each value's part from a part group, then review before anything is written"
                    >
                      Fill from group…
                    </Button>
                  )}
                </div>
              )}
              {option?.values.map((value) => {
                const m = lineMaps.find((x) => x.option_value.toLowerCase() === value.toLowerCase());
                return (
                  <OptionValueRow
                    key={value}
                    ix={ix}
                    bom={bom}
                    lineId={line.id}
                    value={value}
                    mapId={m?.id}
                    componentId={m?.component_item_id}
                    fallback={part}
                    editable={editable}
                    near={near}
                  />
                );
              })}
            </div>
          </Section>
        )}
        {filling && <FillDialog ix={ix} bom={bom} line={line} onClose={() => setFilling(false)} />}

        {lineOverrides.length > 0 && (
          <Section title="Changed for one variant" count={lineOverrides.length}>
            <div className="px-2 pb-2">
              {lineOverrides.map((o) => {
                const v = ix.itemById.get(o.variant_item_id);
                const swapped = ix.itemById.get(o.component_item_id ?? "");
                return (
                  <div key={o.id} className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-hover">
                    <span className="min-w-0 flex-1 truncate">{v?.shopify_variant_title || v?.sku}</span>
                    <span className="font-mono text-[12px]">
                      {o.qty != null ? `qty ${qty(o.qty)}` : ""}{" "}
                      {swapped && (
                        <PaneLink
                          kind="item"
                          id={swapped.id}
                          to={itemPath(swapped)}
                          className="hover:text-accent-text hover:underline"
                          title={`Open ${swapped.sku} beside this line`}
                        >
                          {swapped.sku}
                        </PaneLink>
                      )}
                    </span>
                    {editable && (
                      <button
                        type="button"
                        className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
                        title="Back to the line's own"
                        onClick={() => mutate("bom_override.remove", { id: o.id })}
                      >
                        <X className="size-3.5" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {editable && (
          <div className="mt-4 border-t border-line px-2 pt-3">
            <Button
              variant="danger"
              size="sm"
              icon={<Trash2 className="size-3.5" />}
              onClick={async () => {
                if (await mutate("bom_line.remove", { id: line.id })) close();
              }}
            >
              Remove line
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function OptionValueRow({
  ix,
  bom,
  lineId,
  value,
  mapId,
  componentId,
  fallback,
  editable,
  near,
}: {
  ix: BomIndex;
  bom: BomRow;
  lineId: string;
  value: string;
  mapId?: string;
  componentId?: string;
  fallback: BomItem | undefined;
  editable: boolean;
  near?: readonly (string | null)[];
}) {
  const [picking, setPicking] = useState(false);
  const item = componentId ? ix.itemById.get(componentId) : undefined;
  const pick = (picked: Item) => {
    setPicking(false);
    if (mapId) mutate("bom_option_map.update", { id: mapId, patch: { component_item_id: picked.id } });
    else mutate("bom_option_map.create", { id: crypto.randomUUID(), bom_line_id: lineId, option_value: value, component_item_id: picked.id });
  };
  return (
    <div className="flex items-center gap-2 rounded-md px-1 py-0.5 hover:bg-hover">
      <span className="w-36 shrink-0 truncate text-muted">{value}</span>
      {picking ? (
        <PartPicker
          bom={bom}
          ix={ix}
          near={near}
          autoFocus
          placeholder={`Part for ${value}…`}
          onPick={pick}
          onCancel={() => setPicking(false)}
          className="min-w-0 flex-1"
        />
      ) : (
        <button
          type="button"
          disabled={!editable}
          onClick={() => setPicking(true)}
          className="min-w-0 flex-1 truncate rounded px-1.5 py-1 text-left font-mono text-[12px]"
        >
          {item ? (
            item.sku
          ) : fallback ? (
            <span className="font-sans text-faint">{fallback.sku} (the line's part)</span>
          ) : (
            <span className="font-sans text-danger">No part</span>
          )}
        </button>
      )}
      {item && !picking && (
        <PaneLink kind="item" id={item.id} to={itemPath(item)} className="rounded p-1 text-faint hover:text-text" title={`Open ${item.sku} beside this line`}>
          <ChevronRight className="size-3.5" />
        </PaneLink>
      )}
      {editable && mapId && !picking && (
        <button
          type="button"
          className="rounded p-1 text-faint hover:text-danger"
          title="Remove"
          onClick={() => mutate("bom_option_map.remove", { id: mapId })}
        >
          <X className="size-3.5" />
        </button>
      )}
    </div>
  );
}

// Costs --------------------------------------------------------------------------------------------

const SOURCE_LABEL = { bom: "BOM", std_cost: "std cost", vendor: "vendor", missing: "no cost" } as const;

function CostsTab({ ix, summary, variants, variantId }: { ix: BomIndex; summary: FamilySummary; variants: Item[]; variantId: string | null }) {
  const place = useBomPlace();
  const byId = new Map(summary.variants.map((v) => [v.variant.id, v]));
  const list = variants.flatMap((v) => byId.get(v.id) ?? []);
  const current = (variantId ? byId.get(variantId) : undefined) ?? list[0];
  const rows = useMemo(() => (current ? current.resolved.lines.map((l, i) => ({ ...l, id: l.key, order: i + 1 })) : []), [current]);
  type Row = (typeof rows)[number];
  const columns = useMemo<Column<Row>[]>(
    () => [
      // The BOM's own order, with each phantom's parts under it.
      { key: "order", header: "#", width: "48px", align: "right", value: (l) => l.order, cell: (l) => <span className="text-faint">{l.order}</span> },
      { key: "sku", header: "SKU", width: "170px", value: (l) => l.item.sku, cell: (l) => <span className="font-mono text-[12px]">{l.item.sku}</span> },
      {
        key: "name",
        header: "Name",
        width: "minmax(260px,2fr)",
        value: (l) => l.item.name,
        cell: (l) => (
          <span className="flex min-w-0 items-center gap-1.5" style={{ paddingLeft: l.depth * 18 }}>
            <span className={cx("truncate", l.phantom && "font-medium")}>{l.item.name}</span>
            {l.phantom && <Badge>built in the same order</Badge>}
            {l.stocked && <Badge>built on its own</Badge>}
            {l.overridden && <Badge tone="accent">this variant</Badge>}
            {l.packaging && <Badge>packaging</Badge>}
          </span>
        ),
      },
      { key: "qty", header: "Qty / unit", width: "90px", align: "right", value: (l) => l.qty, cell: (l) => qty(l.qty) },
      { key: "supply", header: "Supply", width: "120px", value: (l) => SUPPLY_LABELS[l.supply] },
      {
        key: "unit",
        header: "Unit cost",
        width: "150px",
        align: "right",
        value: (l) => l.unitCost,
        cell: (l) =>
          l.costSource === "missing" ? (
            <Badge tone="warn">no cost</Badge>
          ) : (
            <span className="flex items-center gap-1.5">
              <span className="text-[11px] text-faint">{SOURCE_LABEL[l.costSource]}</span>
              {!l.costComplete && (
                <span title="Some of its parts have no cost">
                  <AlertTriangle className="size-3.5 text-warn" />
                </span>
              )}
              {money(l.unitCost)}
            </span>
          ),
      },
      {
        key: "ext",
        header: "Cost",
        width: "110px",
        align: "right",
        value: (l) => l.extCost,
        cell: (l) => (l.phantom ? <span className="text-faint">{cost2((l.unitCost ?? 0) * l.qty)}</span> : cost2(l.extCost)),
      },
      {
        key: "onhand",
        header: "Available",
        width: "90px",
        align: "right",
        value: (l) => (l.phantom ? null : onHandFor(ix, l.itemId)),
        cell: (l) =>
          l.phantom ? (
            ""
          ) : (
            <span className={cx(onHandFor(ix, l.itemId) < l.qty && l.supply === "hhl_consigns" && "text-warn")}>{qty(onHandFor(ix, l.itemId))}</span>
          ),
      },
      { key: "ref", header: "Ref", width: "90px" },
    ],
    [ix],
  );

  if (!current) return <EmptyState title="No variants">This product has no live variants in HHL Ops.</EmptyState>;
  const c = current.resolved.cost;
  const price = current.variant.price;
  return (
    <div className="flex h-full min-h-0">
      {list.length > 1 && (
        <div className="w-72 shrink-0 overflow-y-auto border-r border-line py-1">
          {list.map((v) => (
            <button
              key={v.variant.id}
              type="button"
              onClick={() => setSearch({ variant: v.variant.id, [place.tab]: "costs" })}
              className={cx("flex w-full items-center gap-2 px-3 py-1.5 text-left", v === current ? "bg-selected" : "hover:bg-hover")}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate">{v.variant.shopify_variant_title || v.variant.sku}</span>
                <span className="block font-mono text-[11px] text-faint">{v.variant.sku}</span>
              </span>
              <span className="text-right font-mono text-[12px]">
                <span className="block">
                  {cost2(v.resolved.cost.total)}
                  {v.resolved.cost.complete ? "" : "+"}
                </span>
                <span className={cx("block text-[11px]", marginTone(v.margin))}>{v.margin == null ? "—" : pct(v.margin)}</span>
                {v.build.qty != null && <span className="block text-[11px] text-faint">build {qty(v.build.qty)}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1 border-b border-line px-4 py-2 text-[12px]">
          <span className="font-medium">{current.variant.shopify_variant_title || current.variant.name}</span>
          <span className="text-muted">
            HHL consigns <span className="font-mono text-text">{cost2(c.consigned)}</span>
          </span>
          <span className="text-muted">
            CM buys <span className="font-mono text-text">{cost2(c.cmBuys)}</span>
          </span>
          {c.service > 0 && (
            <span className="text-muted">
              Services <span className="font-mono text-text">{cost2(c.service)}</span>
            </span>
          )}
          {(c.charges > 0 || c.perOrder > 0) && (
            <span className="text-muted">
              Charges <span className="font-mono text-text">{cost2(c.charges)}</span>
              {c.perOrder > 0 && <span> + {cost2(c.perOrder)} an order</span>}
            </span>
          )}
          <span className="text-muted">
            Total <span className="font-mono font-medium text-text">{cost2(c.total)}</span>
          </span>
          {price != null && (
            <span className="text-muted">
              Price <span className="font-mono text-text">{money(price)}</span>
            </span>
          )}
          {price != null && (
            <span className="text-muted">
              Margin <span className={cx("font-mono", marginTone(current.margin))}>{current.margin == null ? "—" : pct(current.margin)}</span>
            </span>
          )}
          {!c.complete && <Badge tone="warn">{c.missing} without a cost; not in the total</Badge>}
          <BuildableNote build={current.build} />
        </div>
        {current.resolved.warnings.length > 0 && (
          <div className="shrink-0 border-b border-line bg-danger-soft px-4 py-1.5 text-[12px] text-danger">
            {current.resolved.warnings.map((w) => (
              <div key={w}>{w}</div>
            ))}
          </div>
        )}
        <div className="min-h-0 flex-1">
          <DataTable rows={rows} columns={columns} defaultSort={{ key: "order" }} rowNoun={["line", "lines"]} exportName="BOM costs" pageFilters={false} />
        </div>
      </div>
    </div>
  );
}

/** "Can build 120 now · limited by GCU2-SLV-1404 (120 available, 1 per unit)". */
function BuildableNote({ build }: { build: Buildable }) {
  if (build.qty == null) return <span className="text-faint">Nothing in it comes from HHL's stock</span>;
  return (
    <span
      className="text-muted"
      title="From stock available at HHL (on hand less units committed to customer orders). Stocked sub-assemblies come from their own stock first, then their parts; parts the CM buys don't count."
    >
      Can build <span className={cx("font-mono", build.qty === 0 ? "text-warn" : "text-text")}>{qty(build.qty)}</span> now
      {build.blocked && <span className="text-danger"> · {build.blocked}</span>}
      {build.limiting && (
        <>
          {" "}
          · limited by <span className="font-mono text-text">{build.limiting.item.sku || build.limiting.item.name}</span> ({qty(build.limiting.onHand)}{" "}
          available, {qty(build.limiting.perUnit)} per unit)
        </>
      )}
    </span>
  );
}
